/**
 * Cookie consent utilities for GDPR/CCPA compliance.
 * Use getStoredPreferences() to gate analytics and marketing scripts.
 */

export const STORAGE_KEY = 'motzu_cookie_consent';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  acknowledgedAt: string;
}

/** Custom event fired when user updates cookie preferences */
export const COOKIE_PREFERENCES_CHANGED = 'motzu_cookie_preferences_changed';

/**
 * Returns stored cookie preferences, or null if user hasn't made a choice.
 * Use analytics/marketing flags to decide whether to load tracking scripts.
 */
export const getStoredPreferences = (): CookiePreferences | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as CookiePreferences;
    return parsed?.acknowledgedAt ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Returns true if analytics scripts (e.g. Google Analytics) may be loaded.
 * False when preferences are unknown or analytics was declined.
 */
export const mayLoadAnalytics = (): boolean => {
  const prefs = getStoredPreferences();
  return prefs?.analytics === true;
};

/**
 * Returns true if marketing scripts (e.g. ads, retargeting pixels) may be loaded.
 * False when preferences are unknown or marketing was declined.
 */
export const mayLoadMarketing = (): boolean => {
  const prefs = getStoredPreferences();
  return prefs?.marketing === true;
};
